import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
   "inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
   {
      variants: {
         variant: {
            default:
               "gradient-primary text-black shadow-md hover:shadow-lg hover:scale-[1.02]",
            destructive:
               "bg-[hsl(var(--destructive))] text-white shadow-sm hover:opacity-90",
            outline:
               "border border-[hsl(var(--border))] bg-transparent hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]",
            secondary:
               "bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] hover:opacity-80",
            ghost: "hover:bg-[hsl(var(--accent)/0.1)] hover:text-[hsl(var(--accent-foreground))]",
            link: "text-[hsl(var(--primary))] underline-offset-4 hover:underline",
         },
         size: {
            default: "h-10 px-5 py-2",
            sm: "h-8 rounded-lg px-3 text-xs",
            lg: "h-12 rounded-xl px-8",
            icon: "size-10 rounded-xl",
         },
      },
      defaultVariants: {
         variant: "default",
         size: "default",
      },
   },
);

export interface ButtonProps
   extends
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      VariantProps<typeof buttonVariants> {
   asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
   ({ className, variant, size, asChild = false, ...props }, ref) => {
      const Comp = asChild ? Slot : "button";
      return (
         <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            {...props}
         />
      );
   },
);
Button.displayName = "Button";

export { Button, buttonVariants };
